import notification from "antd/es/notification";
import { isFuture } from "date-fns";
import { isEqual, omit } from "lodash-es";
import { defineMessage, defineMessages } from "react-intl";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { v4 } from "uuid";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import { Activity, Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import File, { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import { isApiError, isDefined, isNotEmpty } from "@mapmycustomers/shared/util/assert";

import { activityAnalytics } from "@app/component/createEditEntity/analytics";
import i18nService from "@app/config/I18nService";
import activitySettings from "@app/scene/activity/utils/activitySettings";
import { getActivityTypes } from "@app/store/activity";
import { notifyActivityRelatedEntities } from "@app/store/activity/actions";
import { ActivityPayload, FieldValidationErrors } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import getNextAssociationsState, {
  NextAssociationsState,
} from "@app/store/associations/getNextAssociationsState";
import { getDealLockGetter } from "@app/store/deal";
import { handleError } from "@app/store/errors/actions";
import {
  doesOrganizationSupportMultiContactActivities,
  getCurrentUser,
  getOrganization,
  getOrganizationId,
} from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import FileListItem from "@app/types/FileListItem";
import getCheckInRecordTypeErrorMap from "@app/util/activity/checkInRecordTypeMessages";
import notifyDealRelinkFailed from "@app/util/deal/notifyDealRelinkFailed";
import notifyDealRelinkLocked from "@app/util/deal/notifyDealRelinkLocked";
import { allSettled, SettleResult } from "@app/util/effects";
import activityFieldModel from "@app/util/fieldModel/ActivityFieldModel";
import { downloadFileByUrl } from "@app/util/file";
import loggingService from "@app/util/logging";
import { parseApiDate } from "@app/util/parsers";

import getSuccessNotificationNode from "../../util/getSuccessNotificationNode";

import { getDeletedFilesList, getFileGroupId, getUploadedFileListIds } from ".";
import {
  bulkCreateActivities,
  changeAssociatedEntities,
  clearAllUploadedCreateEditActivityFiles,
  createActivity,
  deleteActivity,
  downloadActivityFile,
  fetchFilePreview,
  initializeActivityModal,
  initializeEditActivityModal,
  relateUnrelateEntities,
  removeCreateEditActivityFile,
  updateActivity,
  updateActivityModalAssociations,
  uploadCreateEditActivityFiles,
} from "./actions";
import { getAssociatedFiles, getEditedActivity, getNewAssociations } from "./selectors";

const successLoggedMessage = defineMessage({
  id: "createEditActivityModal.logged.success",
  defaultMessage: "Activity logged successfully",
  description: "Activity logged success message",
});

const successUpdatedMessage = defineMessage({
  id: "createEditActivityModal.update.success",
  defaultMessage: "Activity updated successfully",
  description: "Activity updated success message",
});

const successDeletedMessage = defineMessage({
  id: "createEditActivityModal.delete.success.title",
  defaultMessage: "Activity successfully deleted",
  description: "Activity deleted success message",
});

const successDeletedDescription = defineMessage({
  id: "createEditActivityModal.delete.success.description",
  defaultMessage: "To restore this record, go to Data in Settings.",
  description: "Activity deleted success message description",
});

const validationErrorMessages = defineMessages({
  v0204: {
    id: "createEditActivityModal.requiredCF.error",
    defaultMessage: "Please fill in all required custom fields before saving.",
    description: "Reuired custom field error message",
  },
});

const bulkCreateSuccessMessage = defineMessage({
  id: "createEditActivityModal.bulkCreate.success",
  defaultMessage:
    "{count} activities {isFutureDate, select, true {scheduled} other {logged}} successfully",
  description: "Activity bulk created success message",
});

const REQUIRED_FIELD_ERROR_CODE = "v0586";

export function* onInitializeActivityModal({
  payload,
}: ReturnType<typeof initializeActivityModal.request>) {
  try {
    const associationsState: NextAssociationsState = yield call(getNextAssociationsState, {
      assignee: payload.assignee,
      company: payload.fixedCompany,
      deal: payload.fixedDeal,
      entityType: EntityType.ACTIVITY,
      person: payload.fixedPerson,
    });

    yield put(initializeActivityModal.success({ associationsState, fileGroupId: v4() }));
  } catch (error) {
    yield put(initializeActivityModal.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateActivityModalAssociations({
  payload,
}: ReturnType<typeof updateActivityModalAssociations.request>) {
  try {
    const associationsState: NextAssociationsState = yield call(getNextAssociationsState, {
      assignee: payload.assignee,
      company: payload.fixedCompany,
      deal: payload.fixedDeal,
      entityType: EntityType.ACTIVITY,
      person: payload.fixedPerson,
    });

    yield put(updateActivityModalAssociations.success({ associationsState }));
  } catch (error) {
    yield put(updateActivityModalAssociations.failure());
    yield put(handleError({ error }));
  }
}

export function* onInitializeEditActivityModal({
  payload,
}: ReturnType<typeof initializeEditActivityModal.request>) {
  try {
    const { activityId, overrideValues } = payload;
    const org: Organization = yield select(getOrganization);

    const showSharedActivities = activitySettings.getSharedActivitiesVisibility();

    let activity: Activity = yield callApi("fetchActivity", org.id, activityId, {
      includeAccessStatus: true,
      includeCustomFields: true,
      includeFiles: true,
      includeUsersWithAccess: true,
      ...(showSharedActivities
        ? {
            includeAccountSharedActivities: true,
            includeContactSharedActivities: true,
            includeDealSharedActivities: true,
          }
        : {}),
    });
    const activityCompleted = activity.completed;
    if (overrideValues) {
      activity = { ...activity, ...overrideValues };
    }

    yield put(
      initializeEditActivityModal.success({
        activity,
        activityCompleted,
        fileGroupId: v4(),
        files: activity.files as RawFile[],
      })
    );

    yield put(
      initializeActivityModal.request({
        assignee: activity.assignee,
        fixedCompany: activity.account,
        fixedDeal: activity.deal,
        fixedPerson: activity.contact,
      })
    );
  } catch (error) {
    const noAccess = isApiError(error) && error.status === 404;
    yield put(initializeEditActivityModal.failure(noAccess));
    // removed temporary
    // https://mapmycustomers.atlassian.net/browse/TART-7125?atlOrigin=eyJpIjoiZjNkZWViNjI5YzJhNDBkNGFhNTcxMTk3MjE4OWNhN2IiLCJwIjoiamlyYS1zbGFjay1pbnQifQ
    // yield put(handleError({ error }));
  }
}

export function* onChangeAssociatedEntity({
  payload,
}: ReturnType<typeof changeAssociatedEntities.request>) {
  try {
    const currentUser: User = yield select(getCurrentUser);
    const associationsState: NextAssociationsState = yield call(getNextAssociationsState, {
      ...payload,
      assignee: payload.assignee ?? currentUser,
      entityType: EntityType.ACTIVITY,
    });
    yield put(changeAssociatedEntities.success(associationsState));
  } catch (error) {
    yield put(changeAssociatedEntities.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreateActivity({ payload }: ReturnType<typeof createActivity.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const { activity, callback, customFieldsValues, fieldsValueValidateCallback, layoutId } =
      payload;
    const fileIds: File["id"][] = yield select(getUploadedFileListIds);

    // Validate custom fields values
    if (customFieldsValues.length && fieldsValueValidateCallback) {
      const customFieldValidateResponse: FieldValidationErrors[] = yield callApi(
        "validateCustomFieldsValues",
        org.id,
        layoutId,
        EntityType.ACTIVITY,
        undefined,
        customFieldsValues
      );

      if (customFieldValidateResponse.length) {
        yield call(fieldsValueValidateCallback, customFieldValidateResponse);
        yield put(createActivity.failure());
        return;
      }
    }

    const activityToSend: ActivityPayload = {
      ...activity,
      files: fileIds.length > 0 ? [...fileIds] : undefined,
    };
    const newActivity: Activity = yield callApi("createActivity", org.id, layoutId, activityToSend);

    if (customFieldsValues.length) {
      yield callApi(
        "createCustomFieldsValues",
        org.id,
        layoutId,
        EntityType.ACTIVITY,
        newActivity.id,
        customFieldsValues
      );
    }

    const isMultiContactEnabled: boolean = yield select(
      doesOrganizationSupportMultiContactActivities
    );
    if (isMultiContactEnabled && (newActivity.contacts?.length ?? 0) > 1) {
      const activityTypes: ReturnType<typeof getActivityTypes> = yield select(getActivityTypes);
      const activityTypeName = activityTypes.find(
        (type) => type.id === newActivity.crmActivityType.id
      )?.name;
      const companyCount = new Set(
        (newActivity.contacts ?? []).map((person) => person.primaryAccount?.id).filter(isNotEmpty)
      ).size;
      activityAnalytics.completed("Activity Save", {
        activityType: activityTypeName,
        companyCount,
        peopleCount: newActivity.contacts?.length ?? 0,
      });
    }

    callback?.(true, newActivity);
    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(successLoggedMessage, "Activity logged successfully")
      ),
    });

    yield put(notifyAboutChanges({ added: [newActivity], entityType: EntityType.ACTIVITY }));
    yield put(notifyActivityRelatedEntities(newActivity));

    yield put(clearAllUploadedCreateEditActivityFiles());

    yield put(createActivity.success());
  } catch (error) {
    yield put(createActivity.failure());
    yield put(handleError({ error, messageByErrorCodeMap: validationErrorMessages }));
  }
}

export function* onUpdateActivity({ payload }: ReturnType<typeof updateActivity.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    const {
      activity,
      callback,
      customFieldsValues,
      fieldsValueValidateCallback,
      layoutId,
      updateType,
    } = payload;
    const fileIds: File["id"][] = yield select(getUploadedFileListIds);

    const readOnlyFields: string[] = activityFieldModel.fields
      .filter((field) => !field.isEditable || field.hasFeature(FieldFeature.CALCULATED_FIELD))
      .map((field) => field.platformName);

    const strippedActivity = readOnlyFields.length
      ? (omit(activity, readOnlyFields) as Identified)
      : activity;

    const editedActivity: Activity = yield select(getEditedActivity);

    const changedCustomFields = customFieldsValues.filter((customField) => {
      if (readOnlyFields.includes(customField.esKey)) {
        return false;
      }

      const oldCustomField = editedActivity.customFields?.find(
        ({ customField: { id } }) => id === customField.customField.id
      );
      return !oldCustomField || !isEqual(oldCustomField.value, customField.value);
    });

    // Validate custom fields values
    if (changedCustomFields.length && fieldsValueValidateCallback) {
      let customFieldValidateResponse: FieldValidationErrors[] = yield callApi(
        "validateCustomFieldsValues",
        org.id,
        layoutId,
        EntityType.ACTIVITY,
        activity.id,
        changedCustomFields
      );

      if (!activity.completed) {
        customFieldValidateResponse = customFieldValidateResponse
          .map(({ error }) => ({
            error: error.filter(({ code }) => code !== REQUIRED_FIELD_ERROR_CODE),
          }))
          .filter(({ error }) => error.length);
      }

      if (customFieldValidateResponse.length) {
        yield call(fieldsValueValidateCallback, customFieldValidateResponse);
        yield put(updateActivity.failure());
        return;
      }
    }

    const activityToSend: Partial<ActivityPayload> & Identified = {
      ...strippedActivity,
      files: fileIds.length > 0 ? [...fileIds] : undefined,
    };
    yield callApi(
      "updateActivity",
      org.id,
      layoutId,
      activityToSend,
      updateType
        ? {
            updateRecurring: updateType,
          }
        : undefined
    );
    if (changedCustomFields.length) {
      yield callApi(
        "upsertCustomFieldsValues",
        org.id,
        layoutId,
        EntityType.ACTIVITY,
        activity.id,
        changedCustomFields
      );
    }

    const deleteFiles: RawFile[] = yield select(getDeletedFilesList);
    if (deleteFiles.length) {
      yield all(
        deleteFiles.map((file) =>
          callApi("deleteEntityFile", org.id, EntityType.ACTIVITY, activity.id, file.id)
        )
      );
    }

    // we can't use response from updateActivity, since it doesn't include updated custom fields
    // because custom fields are updated in a separate request
    const updatedActivity: Activity = yield callApi("fetchActivity", org.id, activity.id, {
      includeAccessStatus: true,
      includeCustomFields: true,
    });

    callback?.(true, updatedActivity);

    const isMultiContactEnabled: boolean = yield select(
      doesOrganizationSupportMultiContactActivities
    );
    if (isMultiContactEnabled && (updatedActivity.contacts?.length ?? 0) > 1) {
      const companyCount = new Set(
        (updatedActivity.contacts ?? [])
          .map((contact) => contact.primaryAccount?.id)
          .filter(isDefined)
      ).size;
      activityAnalytics.completed("Activity Update", {
        activityId: updatedActivity.id,
        activityType: updatedActivity.crmActivityType?.name,
        companyCount,
        peopleCount: updatedActivity.contacts?.length ?? 0,
      });
    }

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(successUpdatedMessage, "Activity updated successfully")
      ),
    });

    yield put(
      notifyAboutChanges({
        entityType: EntityType.ACTIVITY,
        possiblyOtherRecordsAffected: !!updateType,
        updated: [updatedActivity],
      })
    );
    yield put(notifyActivityRelatedEntities(updatedActivity));

    yield put(clearAllUploadedCreateEditActivityFiles());

    yield put(updateActivity.success(updatedActivity));
  } catch (error) {
    yield put(updateActivity.failure());
    yield put(
      handleError({
        error,
        messageByErrorCodeMap: getCheckInRecordTypeErrorMap(
          payload.activity.checkInType as Activity["checkInType"]
        ),
      })
    );
  }
}

export function* onDeleteActivity({ payload }: ReturnType<typeof deleteActivity.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const { activityId, callback } = payload;
    yield callApi("deleteActivity", org.id, activityId, payload.deleteType);
    callback?.();
    notification.success({
      description: i18nService.formatMessage(successDeletedDescription),
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(successDeletedMessage, "Activity successfully deleted")
      ),
    });

    yield put(notifyAboutChanges({ deletedIds: [activityId], entityType: EntityType.ACTIVITY }));
    yield put(deleteActivity.success());
  } catch (error) {
    yield put(deleteActivity.failure());
    yield put(handleError({ error }));
  }
}

export function* onUploadCreateEditActivityFiles({
  payload,
}: ReturnType<typeof uploadCreateEditActivityFiles.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const fileGroupId: string = yield select(getFileGroupId);
    const responses: SettleResult<RawFile>[] = yield allSettled(
      payload.files.map((file) =>
        callApi("createFile", org.id, file, undefined, undefined, undefined, {
          headers: {
            "x-mmc-file-group-id": fileGroupId,
          },
        })
      )
    );
    const fileList: FileListItem[] = responses.map((response, index) => ({
      file: payload.files[index],
      uploading: false,
      ...(response.error
        ? { errored: true, errorMessage: String(response.result) }
        : { errored: false, uploadedFile: response.result }),
    }));
    payload.callback?.(fileList);
    yield put(uploadCreateEditActivityFiles.success(fileList));
  } catch (error) {
    payload.callback?.(payload.files.map((file) => ({ errored: true, file, uploading: false })));
    yield put(uploadCreateEditActivityFiles.failure());
    yield put(handleError({ error }));
  }
}

export function* onRemoveCreateEditActivityFile({
  payload,
}: ReturnType<typeof removeCreateEditActivityFile.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const associatedFiles: RawFile[] = yield select(getAssociatedFiles);
    const associatedFile = associatedFiles.find(({ id }) => id === payload.id);
    if (!associatedFile) {
      yield callApi("deleteFile", org.id, payload.id);
    }
    yield put(removeCreateEditActivityFile.success(payload));
  } catch (error) {
    yield put(removeCreateEditActivityFile.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchFilePreview({
  payload: fileId,
}: ReturnType<typeof fetchFilePreview.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const activity: Activity = yield select(getEditedActivity);
    const fileData: Blob = yield callApi(
      "fetchFile",
      org.id,
      fileId,
      false,
      true,
      EntityType.ACTIVITY,
      activity.id,
      { responseType: "blob" }
    );
    yield put(fetchFilePreview.success(fileData));
  } catch (error) {
    yield put(fetchFilePreview.failure());
    yield put(handleError({ error }));
  }
}

// An activity's own deal ref can lack the funnel and stage the lock is read from, so the full deal
// is read then. undefined when the deal can't be read: the relink is skipped, and the caller says so.
function* isDealEditable(orgId: Organization["id"], deal: Deal) {
  const getDealLock: ReturnType<typeof getDealLockGetter> = yield select(getDealLockGetter);
  if (deal.funnel && deal.stage) {
    return getDealLock(deal).canEdit;
  }
  try {
    const fullDeal: Deal = yield callApi("fetchDeal", orgId, deal.id);
    return getDealLock(fullDeal).canEdit;
  } catch (error) {
    loggingService.error("Skipped relinking an activity's deal: the deal could not be read", {
      dealId: deal.id,
      error,
    });
    yield put(handleError({ displayNotification: false, error }));
    return undefined;
  }
}

export function* onRelateUnrelateEntities({
  payload: {
    assignee,
    associatedCompany,
    associatedDeal,
    associatedPerson,
    failureCallback,
    isDealCorrectlyRelatedToCompany,
    isDealCorrectlyRelatedToPerson,
    isPersonCorrectlyRelatedToCompany,
    successCallback,
    unrelate,
  },
}: ReturnType<typeof relateUnrelateEntities.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    let person: Person | undefined;
    let deal: Deal | undefined;
    const newAssociations:
      | {
          deal?: { accountId?: Company["id"]; contactId?: Person["id"] };
          personCompanyId?: Company["id"];
        }
      | undefined = yield select(getNewAssociations);

    // a read-only deal keeps its company and person; the activity is saved all the same.
    // An Undo touches the deal only when its relate wrote it.
    const dealNeedsRelink = unrelate
      ? !!newAssociations?.deal
      : !isDealCorrectlyRelatedToCompany || !isDealCorrectlyRelatedToPerson;
    const dealEditable: boolean | undefined =
      !!associatedDeal && dealNeedsRelink ? yield* isDealEditable(orgId, associatedDeal) : true;
    const canRelinkDeal = !!associatedDeal && dealNeedsRelink && dealEditable === true;

    let updatePerson = false,
      updateDeal = false,
      updateCompany = false;

    if (!isPersonCorrectlyRelatedToCompany && associatedPerson && associatedCompany) {
      updatePerson = true;
      updateCompany = true;

      person = yield callApi(
        "updatePerson",
        orgId,
        undefined,
        {
          id: associatedPerson.id,
          accounts: [...associatedPerson.accounts, { id: associatedCompany.id }],
        },
        {
          includeUsersWithAccess: true,
        }
      );
    }

    // Undo Company - Person Relationship
    if (
      isPersonCorrectlyRelatedToCompany &&
      associatedPerson &&
      newAssociations?.personCompanyId &&
      unrelate
    ) {
      updatePerson = true;
      updateCompany = true;

      person = yield callApi(
        "updatePerson",
        orgId,
        undefined,
        {
          id: associatedPerson.id,
          accounts: associatedPerson.accounts.filter(
            ({ id }) => id !== newAssociations?.personCompanyId
          ),
        },
        {
          includeUsersWithAccess: true,
        }
      );
    }

    if (
      (!isDealCorrectlyRelatedToCompany || !isDealCorrectlyRelatedToPerson) &&
      associatedDeal &&
      canRelinkDeal
    ) {
      updateDeal = true;
      updateCompany = !isDealCorrectlyRelatedToCompany && !!associatedCompany;
      updatePerson = !isDealCorrectlyRelatedToPerson && !!associatedPerson;

      deal = yield callApi(
        "updateDeal",
        orgId,
        undefined,
        {
          id: associatedDeal.id,
          ...(!isDealCorrectlyRelatedToCompany && associatedCompany
            ? { account: { id: associatedCompany.id } }
            : {}),
          ...(!isDealCorrectlyRelatedToPerson && associatedPerson
            ? { contact: { id: associatedPerson.id } }
            : {}),
        },
        {
          includeUsersWithAccess: true,
        }
      );
    }

    // Undo Deal - Person,Company Relationship
    if (
      (isDealCorrectlyRelatedToCompany || isDealCorrectlyRelatedToPerson) &&
      associatedDeal &&
      canRelinkDeal &&
      newAssociations?.deal &&
      unrelate
    ) {
      updateDeal = true;

      const dealNewAssociations = newAssociations.deal;
      updateCompany = !!dealNewAssociations?.accountId;
      updatePerson = !!dealNewAssociations?.contactId;

      deal = yield callApi(
        "updateDeal",
        orgId,
        undefined,
        {
          id: associatedDeal.id,
          ...(dealNewAssociations?.accountId ? { account: null } : {}),
          ...(dealNewAssociations?.contactId ? { contact: null } : {}),
        },
        {
          includeUsersWithAccess: true,
        }
      );
    }

    // a needed relink was skipped, by the lock or by a deal read that failed
    if (dealEditable !== true) {
      if (dealEditable === false) {
        notifyDealRelinkLocked();
      } else {
        notifyDealRelinkFailed();
      }
      // nothing was written, so the prompt mustn't turn into "now related"
      if (!updatePerson) {
        yield put(relateUnrelateEntities.failure());
        return;
      }
    }

    const associationsState: NextAssociationsState = yield call(getNextAssociationsState, {
      assignee,
      company: associatedCompany,
      deal: deal ?? associatedDeal,
      entityType: EntityType.ACTIVITY,
      person: person ?? associatedPerson,
    });

    successCallback?.();
    yield put(
      relateUnrelateEntities.success({
        associationsState,
        deal,
        person,
        unrelate: unrelate ?? false,
      })
    );

    if (deal && updateDeal) {
      yield put(notifyAboutChanges({ entityType: EntityType.DEAL, updated: [deal] }));
    }

    if (person && updatePerson) {
      yield put(notifyAboutChanges({ entityType: EntityType.PERSON, updated: [person] }));
    }

    if (associatedCompany && updateCompany) {
      yield put(
        notifyAboutChanges({ entityType: EntityType.COMPANY, updated: [associatedCompany] })
      );
    }
  } catch (error) {
    failureCallback?.();
    yield put(relateUnrelateEntities.failure());
    yield put(handleError({ error }));
  }
}

export function* onDownloadActivityFile({
  payload: file,
}: ReturnType<typeof downloadActivityFile>) {
  try {
    const org: Organization = yield select(getOrganization);
    const activity: Activity = yield select(getEditedActivity);
    const fileBlob: Blob = yield callApi(
      "fetchFile",
      org.id,
      file.id,
      true,
      false,
      EntityType.ACTIVITY,
      activity.id,
      { responseType: "blob" }
    );

    yield call(downloadFileByUrl, window.URL.createObjectURL(fileBlob), file.name);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onBulkCreateActivities({
  payload,
}: ReturnType<typeof bulkCreateActivities.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const { activities, callback, customFieldsValues, fieldsValueValidateCallback, layoutId } =
      payload;
    const fileIds: File["id"][] = yield select(getUploadedFileListIds);

    // Validate custom fields values
    if (customFieldsValues.length && fieldsValueValidateCallback) {
      const customFieldValidateResponse: FieldValidationErrors[] = yield callApi(
        "validateCustomFieldsValues",
        org.id,
        layoutId,
        EntityType.ACTIVITY,
        undefined,
        customFieldsValues
      );

      if (customFieldValidateResponse.length) {
        yield call(fieldsValueValidateCallback, customFieldValidateResponse);
        yield put(createActivity.failure());
        return;
      }
    }

    const activitiesToSend: ActivityPayload[] = activities.map((activity) => ({
      ...activity,
      files: fileIds.length > 0 ? [...fileIds] : undefined,
      layoutId,
    }));

    const response: Array<Activity> = yield callApi(
      "createActivitiesInBulk",
      org.id,
      activitiesToSend
    );

    const entityIds = response.flat().map((entity: Activity) => entity.id);

    if (customFieldsValues?.length) {
      yield callApi(
        "bulkUpsertCustomFieldsValues",
        false,
        org.id,
        EntityType.ACTIVITY,
        entityIds.map((id) => ({ id, layoutId: payload.layoutId })),
        customFieldsValues
      );
    }

    const isFutureDate =
      activities[0].startAt &&
      isFuture(parseApiDate(activities[0].startAt)) &&
      !activities[0].completed;

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(
          bulkCreateSuccessMessage,
          `${entityIds.length} activities ${isFutureDate ? "scheduled" : "logged"} successfully`,
          { count: entityIds.length, isFutureDate }
        )
      ),
    });

    if (callback) {
      yield call(callback);
    }

    yield put(clearAllUploadedCreateEditActivityFiles());

    yield put(bulkCreateActivities.success());
  } catch (error) {
    yield put(bulkCreateActivities.failure());
    yield put(handleError({ error, messageByErrorCodeMap: validationErrorMessages }));
  }
}

export function* createEditActivityModalSaga() {
  yield takeLatest(initializeActivityModal.request, onInitializeActivityModal);
  yield takeLatest(initializeEditActivityModal.request, onInitializeEditActivityModal);
  yield takeLatest(changeAssociatedEntities.request, onChangeAssociatedEntity);
  yield takeLatest(createActivity.request, onCreateActivity);
  yield takeLatest(updateActivity.request, onUpdateActivity);
  yield takeLatest(deleteActivity.request, onDeleteActivity);
  yield takeEvery(uploadCreateEditActivityFiles.request, onUploadCreateEditActivityFiles);
  yield takeEvery(removeCreateEditActivityFile.request, onRemoveCreateEditActivityFile);
  yield takeEvery(fetchFilePreview.request, onFetchFilePreview);
  yield takeEvery(downloadActivityFile, onDownloadActivityFile);
  yield takeEvery(relateUnrelateEntities.request, onRelateUnrelateEntities);
  yield takeLatest(bulkCreateActivities.request, onBulkCreateActivities);
  yield takeLatest(updateActivityModalAssociations.request, onUpdateActivityModalAssociations);
}
